import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import PasswordStrength from "@/components/PasswordStrength";
import PasswordInput from "@/components/PasswordInput";
import { Lock, ArrowLeft, ArrowRight, CheckCircle2, Loader2, AlertCircle } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [err, setErr] = useState("");

  const missingToken = !token || token.length < 16;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setStatus("loading");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setStatus("success");
      setTimeout(() => navigate("/auth?mode=login"), 2500);
    } catch (ex) {
      const msg = formatApiError(ex) || "Reset failed. The link may have expired.";
      setErr(msg);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <section className="max-w-md mx-auto px-6 lg:px-8 py-14" data-testid="reset-password-page">
        <Link
          to="/auth?mode=login"
          className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-brandPink"
        >
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>

        <div className="mt-6">
          <div className="pill bg-brandTeal/15 text-brandTeal">
            <Lock className="w-3.5 h-3.5 mr-1.5" /> Set a new password
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-brandSlate mt-3" data-testid="reset-title">
            Choose a new password
          </h1>
          <p className="text-slate-600 mt-3 leading-relaxed">
            Pick something with at least 8 characters that you&rsquo;ll actually remember.
          </p>
        </div>

        {missingToken ? (
          <div className="card-base p-6 mt-8 bg-rose-50 border-rose-200" data-testid="reset-missing-token">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-rose-600 flex-shrink-0" />
              <div>
                <div className="font-display text-lg font-semibold text-brandSlate">This link is invalid</div>
                <p className="text-sm text-slate-600 mt-1">Request a fresh reset link and try again.</p>
                <Link to="/auth/forgot" className="btn-primary mt-4" data-testid="reset-request-new">
                  Request a new link <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        ) : status === "success" ? (
          <div className="card-base p-6 mt-8 bg-brandTeal/10 border-brandTeal/30" data-testid="reset-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-brandTeal flex-shrink-0" />
              <div>
                <div className="font-display text-lg font-semibold text-brandSlate">Password updated</div>
                <p className="text-sm text-slate-600 mt-1">Taking you back to sign in…</p>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="card-base p-6 md:p-7 mt-8" data-testid="reset-form">
            <label className="block text-sm font-bold text-brandSlate mb-1">New password</label>
            <PasswordInput
              required
              minLength={8}
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="input-base mb-4"
              data-testid="reset-password-input"
              toggleTestId="reset-password-toggle"
              disabled={status === "loading"}
            />
            <PasswordStrength password={password} testId="reset-password-strength" />
            <label className="block text-sm font-bold text-brandSlate mb-1">Confirm new password</label>
            <PasswordInput
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Type it again"
              className="input-base"
              data-testid="reset-confirm-input"
              toggleTestId="reset-confirm-toggle"
              disabled={status === "loading"}
            />
            {err && (
              <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="reset-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={status === "loading"}
              className="btn-primary w-full mt-5 disabled:opacity-60"
              data-testid="reset-submit-btn"
            >
              {status === "loading" ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
              ) : (
                <>Save new password <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}
